import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import SEO from '@/components/SEO';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const normalizeWork = (work = {}) => ({
  id:
    work.id ||
    `${work.artistName || work.artist_name || 'art'}-${work.title || work.artwork_title || Math.random()}`,
  image: work.image || work.artwork_image_url || '',
  title: work.title || work.artwork_title || 'Artwork',
  artistName: work.artistName || work.artist_name || 'KalaManch Artist',
  specialty:
    work.specialty ||
    work.category_label ||
    work.category ||
    'Curated Exhibit',
  city: work.city || '',
  regionLabel: work.regionLabel || work.region_label || '',
  description: work.description || '',
  profilePath: work.profilePath || work.artist_profile_path || '/artists',
});

export default function ArtExhibition() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedWork, setSelectedWork] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchWorks = async () => {
      try {
        const response = await axios.get(`${API}/art-exhibition/works`);
        const remoteWorks = Array.isArray(response.data?.works)
          ? response.data.works
          : [];
        if (mounted) {
          setWorks(remoteWorks.map(normalizeWork).filter((work) => work.image));
          setError('');
        }
      } catch (err) {
        if (mounted) {
          setWorks([]);
          setError('The exhibition wall could not load right now.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchWorks();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredWorks = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return works;

    return works.filter((work) =>
      [
        work.title,
        work.artistName,
        work.specialty,
        work.city,
        work.regionLabel,
        work.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(clean)
    );
  }, [query, works]);

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title="KalaManch Art Exhibition | Curated Indian Art Gallery"
        description="Browse the KalaManch Art Exhibition, an admin-curated gallery wall of Indian artworks with artist names, art forms, cities, and public artist profile links."
        keywords="KalaManch art exhibition, Indian art gallery, curated Indian artists, Indian painting gallery, rangoli art, pottery art, traditional Indian art"
      />

      <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl">
          <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="bg-gradient-to-br from-[#0F172A] via-[#10233e] to-[#184b68] px-6 py-10 text-white sm:px-10">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#FDBA74]">
                Admin-managed gallery
              </p>
              <h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl">
                KalaManch Art Exhibition
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-200">
                A public exhibition wall for artworks selected by KalaManch
                admin. Each exhibit shows the artwork, artist name, art form,
                city, and a direct profile link.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[1.4rem] border border-white/10 bg-white/10 px-4 py-4">
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FDBA74]">
                    How it works
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-200">
                    Admin adds, edits, publishes, hides, reorders, or deletes
                    artwork from the Art Exhibition section in admin.
                  </p>
                </div>
                <div className="rounded-[1.4rem] border border-white/10 bg-white/10 px-4 py-4">
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FDBA74]">
                    Separate section
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-200">
                    This page is separate from competitions and Stars of Indian
                    Art. It is only for the curated exhibition wall.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#fff7ed] via-white to-[#ebfff8] px-6 py-10 sm:px-10">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">
                Search the gallery
              </p>
              <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-sm">
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00]"
                  placeholder="Search by artist name, artwork title, city, or art form"
                />
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-700">
                  {loading
                    ? 'Loading gallery...'
                    : `${filteredWorks.length} work(s) on display`}
                </span>
                <span className="rounded-full border border-orange-100 bg-orange-50 px-4 py-2 text-sm font-black text-[#C45A00]">
                  Mixed artists, one exhibition wall
                </span>
              </div>
              <p className="mt-5 text-sm leading-7 text-gray-600">
                Admin can upload an artwork image directly, select an artist, add
                a short title/category/description, and publish it here.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-5 rounded-[1.4rem] border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center text-gray-500 shadow-sm">
            Loading the exhibition wall...
          </div>
        ) : filteredWorks.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center text-gray-500 shadow-sm">
            No published exhibition artworks yet.
          </div>
        ) : (
          <div className="columns-1 gap-5 sm:columns-2 xl:columns-3">
            {filteredWorks.map((work) => (
              <article
                key={work.id}
                className="mb-5 break-inside-avoid overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <button
                  type="button"
                  onClick={() => setSelectedWork(work)}
                  className="block w-full text-left"
                >
                  <img
                    src={work.image}
                    alt={work.title}
                    className="w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </button>

                <div className="space-y-3 p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
                      Curated Exhibit
                    </span>
                    {work.specialty ? (
                      <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-black text-[#C45A00]">
                        {work.specialty}
                      </span>
                    ) : null}
                  </div>

                  <div>
                    <h2 className="text-xl font-black leading-tight text-gray-950">
                      {work.title}
                    </h2>
                    <p className="mt-2 text-base font-black text-[#4A148C]">
                      {work.artistName}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-500">
                      {[work.city, work.regionLabel].filter(Boolean).join(' | ')}
                    </p>
                  </div>

                  {work.description ? (
                    <p className="line-clamp-3 text-sm leading-6 text-gray-600">
                      {work.description}
                    </p>
                  ) : null}

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setSelectedWork(work)}
                    >
                      View artwork
                    </button>
                    <Link to={work.profilePath} className="btn-outline">
                      Artist profile
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selectedWork ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-4 py-6"
          onClick={() => setSelectedWork(null)}
        >
          <div
            className="max-h-[92vh] w-full max-w-6xl overflow-auto rounded-[2rem] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grid gap-0 lg:grid-cols-[0.64fr_0.36fr]">
              <div className="bg-[#07111F]">
                <img
                  src={selectedWork.image}
                  alt={selectedWork.title}
                  className="h-full max-h-[84vh] w-full object-contain"
                />
              </div>

              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6F00]">
                      Exhibition work
                    </p>
                    <h3 className="mt-3 text-3xl font-black leading-tight text-gray-950">
                      {selectedWork.title}
                    </h3>
                    <p className="mt-2 text-lg font-black text-[#4A148C]">
                      {selectedWork.artistName}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedWork(null)}
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm font-black text-slate-600"
                  >
                    Close
                  </button>
                </div>

                <div className="mt-6 space-y-4 text-sm leading-7 text-gray-700">
                  <p>
                    <span className="font-black text-gray-950">Art form:</span>{' '}
                    {selectedWork.specialty || 'Indian Art'}
                  </p>
                  <p>
                    <span className="font-black text-gray-950">Location:</span>{' '}
                    {[selectedWork.city, selectedWork.regionLabel]
                      .filter(Boolean)
                      .join(' | ') || 'India'}
                  </p>
                  {selectedWork.description ? (
                    <p>{selectedWork.description}</p>
                  ) : null}
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to={selectedWork.profilePath}
                    className="btn-primary"
                    onClick={() => setSelectedWork(null)}
                  >
                    Open artist profile
                  </Link>
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setSelectedWork(null)}
                  >
                    Close artwork
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
